import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Separator } from "../components/ui/separator";
import { ArrowLeft, ArrowRight, Clock, Users, CheckCircle, Prohibit, CaretDown, CaretUp, GraduationCap } from "@phosphor-icons/react";
import api from "../lib/api";
import { APPLY_GOOGLE_FORM_URL } from "../lib/applyLinks";
import EnrollIntentModal from "../components/EnrollIntentModal";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { isEnrolling } from "../lib/programmeStatus";

const SBL_LOGO = "/sbl-logo.jpeg";

export default function ProgrammesCataloguePage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [exchangeRate, setExchangeRate] = useState(null);
  const [intentOpen, setIntentOpen] = useState(false);
  const [intentProg, setIntentProg] = useState(null);

  // Click handler for the public "Enrol" button — logged-in students claim
  // the pending enrolment immediately; visitors get a sign-in/sign-up modal.
  const onEnrolClick = async (prog) => {
    if (!user) {
      setIntentProg(prog);
      setIntentOpen(true);
      return;
    }
    if (user.role !== "student" && user.role !== "alumni") {
      toast.info("Only students can enrol in programmes.");
      return;
    }
    try {
      const { data } = await api.post("/enrollments/claim-pending", { programme_id: prog.id });
      toast.success(data.created ? `Added ${prog.code || "programme"} to your dashboard` : "You are already enrolled — opening your dashboard");
      navigate("/my-programmes");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not enrol"); }
  };
  const [expanded, setExpanded] = useState(null);
  const [programmes, setProgrammes] = useState([]);
  const [section, setSection] = useState("all");

  // Apply Now destination depends on auth state.
  const applyHref = (programmeId) => {
    if (!programmeId) return "/programmes-catalogue";
    if (!user) return `/register?mode=programme&programme=${programmeId}`;
    if (user.role === "student") return `/apply/${programmeId}`;
    return `/programmes/${programmeId}`;
  };

  useEffect(() => {
    api.get("/exchange-rate").then(r => setExchangeRate(r.data?.usd_to_ngn)).catch(() => {});
    // Section 3 — fetch only public (active, non-draft, non-archived) programmes
    api.get("/public-programmes")
      .then(r => setProgrammes(r.data || []))
      .catch((err) => console.error("Failed to fetch public programmes:", err));
  }, []);

  const formatNGN = (usd) => {
    if (!exchangeRate || !usd) return "";
    return `NGN ${Math.round(usd * exchangeRate).toLocaleString()}`;
  };

  // Round 30 — Single source of truth: programme.status. The catalogue
  // mirrors the canonical status string ('active'/'inactive') so every
  // downstream check stays in sync with the rest of the site.
  const normalised = programmes.map((p, idx) => {
    const enrolling = isEnrolling(p);
    return {
      id: p.id || `p${idx}`,
      code: p.code || "",
      title: p.title || "Untitled",
      duration: p.duration || "—",
      level: p.level || "",
      nqf_level: p.nqf_level || "",
      section: p.section || "section-a",
      status: enrolling ? "active" : "inactive",
      // Preserve raw status so downstream pages can distinguish inactive vs draft.
      raw_status: (p.status || "").toLowerCase(),
      price: p.price_usd || null,
      discount_percent: p.discount_percent || 0,
      start_date: p.start_date || "",
      end_date: p.end_date || "",
      tagline: p.level ? `${p.level} · ${p.nqf_level || ""}`.trim() : "",
      desc: p.overview || "",
      idealFor: Array.isArray(p.ideal_for)
        ? p.ideal_for
        : (typeof p.ideal_for === 'string' && p.ideal_for.trim()
          ? p.ideal_for.split(',').map(s => s.trim()).filter(Boolean)
          : []),
      courses: [],
      outcome: "",
    };
  });

  const filtered = section === "all" ? normalised
    : section === "a" ? normalised.filter(p => p.section === "section-a")
    : normalised.filter(p => p.section === "section-b");

  const active = filtered.filter(p => p.status === "active");
  const inactive = filtered.filter(p => p.status === "inactive");
  const sorted = [...active, ...inactive];

  return (
    <div className="min-h-screen bg-white">
      <header className="sticky top-0 z-50 bg-white/90 backdrop-blur-xl border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img src={SBL_LOGO} alt="SBL" className="w-9 h-9 object-contain rounded-sm" />
            <span className="text-base sm:text-lg font-bold tracking-tight text-slate-950" style={{ fontFamily: "'Outfit', sans-serif" }}>School Builder Lab Africa</span>
          </Link>
          <Link to="/" className="text-sm text-slate-600 hover:text-slate-900 flex items-center gap-1"><ArrowLeft size={14} /> Back to Home</Link>
        </div>
      </header>

      <div className="bg-slate-950 text-white py-14 sm:py-18">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 text-center">
          <p className="text-xs font-semibold uppercase tracking-[0.25em] text-amber-400 mb-3">Programmes & Courses</p>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight" style={{ fontFamily: "'Outfit', sans-serif" }}>Explore Our Programmes</h1>
          <p className="text-base text-white/70 mt-4 max-w-2xl mx-auto">
            A complete non-instructive school leadership ecosystem — from foundation certificates to executive fellowships. Aligned with NQF Levels 4–8, spanning operations, growth, finance, technology, welfare, governance and beyond.
          </p>
        </div>
      </div>

      {/* Section filter tabs */}
      <div className="bg-slate-50 border-b border-slate-200 sticky top-16 z-30">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-3 flex gap-2 flex-wrap" data-testid="catalogue-section-tabs">
          <button onClick={() => setSection("all")} data-testid="cat-tab-all" className={`px-4 py-2 text-xs sm:text-sm font-bold uppercase tracking-wider border ${section === "all" ? "bg-slate-950 text-white border-slate-950" : "border-slate-300 text-slate-700 hover:border-slate-950"}`}>All ({normalised.length})</button>
          <button onClick={() => setSection("a")} data-testid="cat-tab-a" className={`px-4 py-2 text-xs sm:text-sm font-bold uppercase tracking-wider border ${section === "a" ? "bg-blue-800 text-white border-blue-800" : "border-slate-300 text-slate-700 hover:border-blue-800"}`}>Leadership & Ops ({normalised.filter(p => p.section === "section-a" || p.section === "a").length})</button>
          <button onClick={() => setSection("b")} data-testid="cat-tab-b" className={`px-4 py-2 text-xs sm:text-sm font-bold uppercase tracking-wider border ${section === "b" ? "bg-amber-600 text-white border-amber-600" : "border-slate-300 text-slate-700 hover:border-amber-600"}`}>Foundation & CPD ({normalised.filter(p => p.section === "section-b" || p.section === "b").length})</button>
        </div>
      </div>

      <section className="py-12 sm:py-16" data-testid="programmes-catalogue">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 space-y-5">
          {sorted.map((prog, i) => {
            const isActive = prog.status === "active";
            const isInactive = prog.status === "inactive";
            const isExpanded = expanded === prog.id;
            return (
              <Card key={prog.id} className={`border rounded-none shadow-sm overflow-hidden transition-all ${isActive ? "border-emerald-300 bg-emerald-50/20" : "border-slate-200 bg-slate-50 opacity-75"}`} data-testid={`catalogue-programme-${i}`}>
                <CardContent className="p-0">
                  {/* Header — always visible, clickable */}
                  <button
                    className="w-full text-left px-5 sm:px-7 py-5 flex items-start sm:items-center justify-between gap-4 hover:bg-slate-50/50 transition-colors"
                    onClick={() => setExpanded(isExpanded ? null : prog.id)}
                    data-testid={`programme-toggle-${i}`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <Badge className={`rounded-none text-[10px] font-bold uppercase tracking-widest ${isActive ? "bg-emerald-600 text-white" : (isInactive ? "bg-slate-500 text-white" : "bg-slate-400 text-white")}`}>
                          {isActive ? "Now Enrolling" : (isInactive ? "Not In Session" : "Coming Soon")}
                        </Badge>
                        {prog.code && <span className="text-[10px] font-bold uppercase tracking-widest text-amber-700">{prog.code}</span>}
                        <span className="text-xs text-slate-500 flex items-center gap-1"><Clock size={11} /> {prog.duration}</span>
                        {prog.nqf_level && <span className="text-[10px] uppercase tracking-wider text-slate-500">· {prog.nqf_level}</span>}
                      </div>
                      <h2 className="text-base sm:text-lg font-bold text-slate-950 leading-tight" style={{ fontFamily: "'Outfit', sans-serif" }}>{prog.title}</h2>
                      <p className="text-xs sm:text-sm text-slate-500 mt-0.5 italic">{prog.tagline}</p>
                      {prog.code && (
                        <Link to={`/p/${prog.code.toLowerCase()}`} onClick={e => e.stopPropagation()} className="inline-flex items-center gap-1 mt-2 text-xs font-bold text-blue-800 hover:underline" data-testid={`view-details-${prog.code}`}>
                          View full programme page <ArrowRight size={11} weight="bold" />
                        </Link>
                      )}
                    </div>
                    <div className="flex-shrink-0 text-slate-400 mt-1">
                      {isExpanded ? <CaretUp size={20} /> : <CaretDown size={20} />}
                    </div>
                  </button>

                  {/* Expanded detail */}
                  {isExpanded && (
                    <div className="px-5 sm:px-7 pb-6 border-t border-slate-100 pt-5 space-y-5">
                      <p className="text-sm text-slate-700 leading-relaxed">{prog.desc}</p>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                        <div>
                          <p className="text-xs font-bold uppercase tracking-widest text-blue-800 mb-2 flex items-center gap-1"><Users size={12} /> Ideal For</p>
                          <ul className="space-y-1">
                            {prog.idealFor.map((item, j) => (
                              <li key={j} className="text-xs text-slate-600 flex items-center gap-2">
                                <span className="w-1 h-1 bg-blue-800 rounded-full flex-shrink-0" />{item}
                              </li>
                            ))}
                          </ul>
                        </div>
                        <div>
                          <p className="text-xs font-bold uppercase tracking-widest text-blue-800 mb-2 flex items-center gap-1"><GraduationCap size={12} /> Core Courses</p>
                          <ul className="space-y-1">
                            {prog.courses.map((c, j) => (
                              <li key={j} className="text-xs text-slate-600 flex items-center gap-2">
                                <CheckCircle size={11} className="text-emerald-600 flex-shrink-0" weight="fill" />{c}
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>

                      <div className="bg-slate-50 border border-slate-200 p-4">
                        <p className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-1">Outcome</p>
                        <p className="text-sm text-slate-700">{prog.outcome}</p>
                      </div>

                      <Separator />

                      {isActive ? (
                        <div className="flex items-center justify-between flex-wrap gap-4">
                          <div>
                            <div className="flex items-baseline gap-2">
                              <span className="text-3xl font-bold text-slate-950" style={{ fontFamily: "'Outfit', sans-serif" }}>${prog.price}</span>
                              {exchangeRate && <span className="text-lg text-slate-600 font-semibold">({formatNGN(prog.price)})</span>}
                            </div>
                            <p className="text-xs text-slate-500 font-semibold mt-0.5">Programme fee · USD or Naira equivalent</p>
                            <p className="text-xs text-slate-500 mt-0.5">Minimum 60% upfront &middot; Balance due within 6 weeks of programme start</p>
                            <div className="mt-2 text-xs text-slate-500">
                              <p className="font-medium text-slate-700 mb-0.5">Payment Accounts (Providus Bank):</p>
                              <p>Naira: <span className="font-mono font-semibold">1309466171</span> &middot; Dollar: <span className="font-mono font-semibold">1309470279</span></p>
                            </div>
                          </div>
                          <div className="flex gap-2 flex-wrap">
                            <Button
                              onClick={() => onEnrolClick(prog)}
                              className="bg-emerald-600 text-white hover:bg-emerald-700 rounded-none text-sm font-semibold px-6 h-11 gap-2"
                              data-testid={`catalogue-enrol-${i}`}
                            >
                              Enrol <ArrowRight size={14} />
                            </Button>
                            <a href={APPLY_GOOGLE_FORM_URL} target="_blank" rel="noopener noreferrer">
                              <Button variant="outline" className="rounded-none text-sm font-semibold px-6 h-11 gap-2" data-testid="catalogue-apply-btn">
                                Apply Now <ArrowRight size={14} />
                              </Button>
                            </a>
                          </div>
                        </div>
                      ) : (
                        <div className="space-y-3">
                          <div className="bg-slate-50 border border-slate-200 p-3 text-sm text-slate-600">
                            <p className="font-medium text-slate-800 mb-1">This programme is not currently in session.</p>
                            <p>We recommend starting with programmes that are currently in session to build your leadership foundation while you wait. Reach out to us to discuss your goals and find the best fit.</p>
                          </div>
                          <Link to="/#contact">
                            <Button variant="outline" className="rounded-none text-sm gap-2" data-testid={`catalogue-enquiry-${i}`}>
                              Make Enquiry <ArrowRight size={14} />
                            </Button>
                          </Link>
                        </div>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}

          {/* Programme Guide */}
          <Card className="border border-blue-200 rounded-none bg-blue-50/30 mt-8">
            <CardContent className="p-6 sm:p-8">
              <h3 className="text-base font-bold text-slate-950 mb-4" style={{ fontFamily: "'Outfit', sans-serif" }}>How to Choose Your Pathway</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
                {[
                  ["Entering leadership?", "Programme 1"],
                  ["Run operations?", "Programme 2"],
                  ["Lead people or welfare?", "Programme 3 or 8"],
                  ["Want school growth?", "Programme 4"],
                  ["Manage compliance & controls?", "Programme 5"],
                  ["Lead technology?", "Programme 6"],
                  ["Run support units?", "Programme 7"],
                  ["Supervise many departments?", "Programme 9"],
                  ["Ready for elite transformation?", "Programme 10"],
                ].map(([q, a], j) => (
                  <div key={j} className="flex items-center gap-2 text-slate-600">
                    <ArrowRight size={12} className="text-blue-800 flex-shrink-0" />
                    <span><strong>{q}</strong> {a}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      <footer className="bg-slate-950 text-slate-400 py-8 text-center text-xs">
        <p>&copy; {new Date().getFullYear()} School Builder Lab Africa. All rights reserved.</p>
      </footer>
      <EnrollIntentModal open={intentOpen} onOpenChange={setIntentOpen} programme={intentProg} />
    </div>
  );
}
